import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BASE_DOMAIN_URI, llm_train_uri } from 'app/app.config';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LlmService {

  model2_base_url = `${llm_train_uri.model2_url}` + "/absenceassistance-library/";
  model1_base_url = `${llm_train_uri.model1_url}` + "/cacall_processnotes/";

  Disclaimer = "<b>Disclaimer:</b> <span>The information provided from this tool is not for legal advice, and is intended to provide references.</span>";

  LLM_classes: any[] = [];
  LLM_user_list: any[] = [];

  selectedDoc_ids: any[] = [];
  selectedDoc_name_map: any = {};
  selectedDoc_summary_category_map: any = {};

  llm_bot_payload: any = {
    mode: '',
    class_id: '',
    model_type: '',
    document_id: []
  };

  private jsonHeaders = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) {
    this.restoreLLMBotPayload();
    this.restoreSelectedDocs();
  }

  // ===============================
  // LocalStorage Functions
  // ===============================

  saveLLMBotPayload() {
    try {
      localStorage.setItem('llm_bot_payload', JSON.stringify(this.llm_bot_payload));
    } catch (error) {
      console.error('Error saving llm_bot_payload:', error);
    }
  }

  restoreLLMBotPayload() {
    try {
      const savedPayload = localStorage.getItem('llm_bot_payload');

      if (!savedPayload) return;

      const parsed = JSON.parse(savedPayload);

      if (parsed && typeof parsed === 'object') {
        this.llm_bot_payload = {
          mode: parsed.mode ?? '',
          class_id: parsed.class_id ?? '',
          model_type: parsed.model_type ?? '',
          document_id: parsed.document_id ?? []
        };
      }

    } catch (error) {
      console.error('Error restoring llm_bot_payload:', error);
    }
  }

  clearLLMBotPayload() {
    try {
      localStorage.removeItem('llm_bot_payload');

      this.llm_bot_payload = {
        mode: '',
        class_id: '',
        model_type: '',
        document_id: []
      };

    } catch (error) {
      console.error('Error clearing llm_bot_payload:', error);
    }
  }

  saveSelectedDocs() {
    try {
      const data = {
        selectedDoc_ids: this.selectedDoc_ids,
        selectedDoc_name_map: this.selectedDoc_name_map,
        selectedDoc_summary_category_map: this.selectedDoc_summary_category_map
      };

      localStorage.setItem('llm_selected_docs', JSON.stringify(data));

    } catch (error) {
      console.error('Error saving selected docs:', error);
    }
  }

  restoreSelectedDocs() {
    try {
      const saved = localStorage.getItem('llm_selected_docs');

      if (!saved) return;

      const parsed = JSON.parse(saved);

      if (parsed) {
        this.selectedDoc_ids = parsed.selectedDoc_ids || [];
        this.selectedDoc_name_map = parsed.selectedDoc_name_map || {};
        this.selectedDoc_summary_category_map = parsed.selectedDoc_summary_category_map || {};
      }

    } catch (error) {
      console.error('Error restoring selected docs:', error);
    }
  }

  clearSelectedDocs() {
    try {

      localStorage.removeItem('llm_selected_docs');

      this.selectedDoc_ids = [];
      this.selectedDoc_name_map = {};
      this.selectedDoc_summary_category_map = {};

    } catch (error) {
      console.error('Error clearing selected docs:', error);
    }
  }

  // ===============================
  // User APIs
  // ===============================

  llmCreateUser(payload: any) {
    return this.http.post(this.model2_base_url + 'create-user', payload);
  }

  llmUserLogin(payload: any) {
    return this.http.post(this.model2_base_url + 'login', payload);
  }

  llmUserLogout(payload: any) {
    return this.http.post(this.model2_base_url + 'logout', payload);
  }

  llmUserAddClassAccess(payload: any) {
    return this.http.post(this.model2_base_url + 'grant-class-access', payload);
  }

  llmUserRemoveClassAccess(payload: any) {
    return this.http.post(this.model2_base_url + 'revoke-class-access', payload);
  }

  llmUserInfo() {
    return this.http.get(this.model2_base_url + 'user-info', this.getModel2Headers());
  }

  llmUserList() {
    const payload = { client_id: localStorage.getItem("company_id") };
    return this.http.post(this.model2_base_url + 'list-users', payload, this.getModel2Headers());
  }

  // ===============================
  // Document APIs
  // ===============================

  llmTrainDoc(formData: any) {
    return this.http.post(this.model2_base_url + 'receive_document', formData, this.getModel2Headers());
  }

  llmUploadDocAss(formData: any) {
    return this.http.post(this.model2_base_url + 'update', formData, this.getModel2Headers());
  }

  llmTrainDocUpdate(formData: any, llm_unqId: string) {
    return this.http.put(this.model2_base_url + 'update-document/' + llm_unqId, formData, this.getModel2Headers());
  }

  llmTrainDocDelete(llm_unqId: string) {
    return this.http.delete(this.model2_base_url + 'delete_document/' + llm_unqId, this.getModel2Headers());
  }

  // ===============================
  // Query APIs
  // ===============================

  private getModel2Headers() {
    return {
      headers: new HttpHeaders({
        'Authorization': 'Bearer ' + localStorage.getItem('llm_token')
      })
    }
  }

  llmQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'query', payload, this.getModel2Headers());
  }

  llmQuery_v2(payload: any) {
    return this.http.post(this.model2_base_url + 'query_v2', payload, this.getModel2Headers());
  }

  llmGenericQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'generic_query', payload, this.getModel2Headers());
  }

  llmSimpleQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'query_simple', payload, this.getModel2Headers());
  }

  llmSimpleQuery_v2(payload: any) {
    return this.http.post(this.model2_base_url + 'simple_query_v2', payload, this.getModel2Headers());
  }

  llmAnalyzeQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'analyze-query', payload, this.getModel2Headers());
  }

  // ===============================
  // Bharathi LLM
  // ===============================

  private getJsonTokenHeader() {
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhcGlfdXNlciIsImV4cCI6MTgwNTYzNDQwN30.-WJOHvnGN47tyk6R6jrKSi3JmlUw_jujGPCrs2fVb70`
      })
    }
  }

  llmsearchAndQAUpload(formData: any) {
    return this.http.post(this.model1_base_url + 'upload', formData, this.getJsonTokenHeader());
  }

  llmsearchAndQAUploadSearch(formData: any) {
    return this.http.post(this.model1_base_url + 'query', formData, this.getJsonTokenHeader());
  }

  llmsearchBydocId(doc_id: string) {
    return this.http.get(this.model1_base_url + 'documents/' + doc_id, this.getJsonTokenHeader());
  }

  llmdeleteBydocId(doc_id: string) {
    return this.http.delete(this.model1_base_url + 'delete/' + doc_id, this.getJsonTokenHeader());
  }

  llmModel1SummaryBydocId(doc_id:string) {
    return this.http.get(this.model1_base_url + 'summary', this.getJsonTokenHeader())
  }

  //Contract LLM
  llmsearchContractSearch(formData: any) {

    return this.http.post(
      this.model1_base_url + 'detailed_query',
      formData,
      this.getJsonTokenHeader()
    );
  }
  // ===============================
  // Class APIs
  // ===============================

  llmDeleteClass(classid: string) {
    const payload = { class_id: classid };
    return this.http.post(this.model2_base_url + 'delete-class', payload, this.getModel2Headers());
  }

  llmClassInfo(param: string) {
    return this.http.get(this.model2_base_url + 'class-info?class_id=' + param, this.getModel2Headers());
  }

  // ===============================
  // Assessment APIs
  // ===============================

  llmQuestionGenerate(payload: any) {
    return this.http.post(this.model2_base_url + 'generate-assessment', payload, this.getModel2Headers());
  }

  llmQuestionGeneratebyDoc(payload: any) {
    return this.http.post(this.model2_base_url + 'generate-document-assessment', payload, this.getModel2Headers());
  }

  // ===============================
  // Health API
  // ===============================

  llmRunningStarus() {
    return this.http.get(this.model2_base_url + 'health');
  }

  // ===============================
  // RFP Upload
  // ===============================

  llmRfpQAUpload(formData: any) {
    return this.http.post(
      BASE_DOMAIN_URI + '/pdf/rfp_q_a',
      formData,
      { responseType: 'blob', observe: 'response' }
    );
  }

}
